import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantTerminology.css";
import { API_BASE } from "../../../ApiUrl";
import SettingsTabs from "../Settings/SettingsTabs";

const API_URL = `${API_BASE}/tenant-terminology/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const CANONICAL_CODE_SUGGESTIONS = [
  "PROJECT",
  "TASK",
  "INSPECTION",
  "REPORT",
  "CLIENT",
  "SITE",
  "ASSET",
  "TEAM",
  "MEMBER",
  "DOCUMENT"
];

const emptyTerm = {
  id: null,
  canonical_code: "",
  display_label: ""
};

const AddTenantTerminology = () => {
  const navigate = useNavigate();

  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([{ ...emptyTerm }]);
  const [tenantId, setTenantId] = useState(null);

  // ----------------------------------------------------------
  // GET TENANT ID
  // ----------------------------------------------------------

  useEffect(() => {
    const stored = localStorage.getItem("tenant_user");
    if (!stored) {
      Swal.fire({
        icon: "error",
        title: "Tenant information missing",
        text: "Please login again."
      });
      return;
    }

    try {
      const tenantUser = JSON.parse(stored);
      if (!tenantUser.tenant_id) {
        Swal.fire({
          icon: "error",
          title: "Tenant ID missing",
          text: "Tenant information could not be found."
        });
        return;
      }
      setTenantId(Number(tenantUser.tenant_id));
    } catch (error) {
      console.error("Invalid tenant_user:", error);
      Swal.fire({
        icon: "error",
        title: "Invalid tenant session",
        text: "Please login again."
      });
    }
  }, []);

  // ----------------------------------------------------------
  // FETCH TERMINOLOGY
  // ----------------------------------------------------------

  useEffect(() => {
    if (tenantId) fetchTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchTerms = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["tenant_terminology"]
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.tenant_terminology) {
        rows = result.data.tenant_terminology;
      } else if (result?.tenant_terminology) {
        rows = result.tenant_terminology;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      setTerms(rows);
    } catch (error) {
      console.error("Error fetching terminology:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load terminology",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading terminology."
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // FORM HANDLERS
  // ----------------------------------------------------------

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev.map((t, i) => (i === index ? { ...t, [name]: value } : t))
    );
  };

  const handleAdd = () => {
    setFormData([{ ...emptyTerm }]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [...prev, { ...emptyTerm }]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (term) => {
    setFormData([
      {
        id: term.id,
        canonical_code: term.canonical_code || "",
        display_label: term.display_label || ""
      }
    ]);
    setEditingId(term.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (term) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Term?",
      text: `Are you sure you want to delete "${
        term.canonical_code || "this term"
      }"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}${term.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Terminology deleted successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      fetchTerms();
    } catch (error) {
      console.error("Error deleting term:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the terminology."
      });
    }
  };

  // ----------------------------------------------------------
  // VALIDATION
  // ----------------------------------------------------------

  const validateRow = (t, index) => {
    const label = `Term ${index + 1}`;

    const isEmptyRow =
      !t.canonical_code.trim() && !t.display_label.trim() && !t.id;
    if (isEmptyRow) return true;

    if (!t.canonical_code.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Canonical Code Required",
        text: `Please provide a Canonical Code for ${label}.`
      });
      return false;
    }

    if (t.canonical_code.trim().length > 100) {
      Swal.fire({
        icon: "warning",
        title: "Canonical Code Too Long",
        text: `Canonical Code for ${label} must be 100 characters or fewer.`
      });
      return false;
    }

    if (!t.display_label.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Display Label Required",
        text: `Please provide a Display Label for ${label}.`
      });
      return false;
    }

    if (t.display_label.trim().length > 100) {
      Swal.fire({
        icon: "warning",
        title: "Display Label Too Long",
        text: `Display Label for ${label} must be 100 characters or fewer.`
      });
      return false;
    }

    return true;
  };

  const buildPayload = (t) => ({
    canonical_code: t.canonical_code.trim().toUpperCase(),
    display_label: t.display_label.trim(),
    tenant: Number(tenantId)
  });

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  // ----------------------------------------------------------
  // SUBMIT
  // ----------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant."
      });
      return;
    }

    for (let i = 0; i < formData.length; i++) {
      if (!validateRow(formData[i], i)) return;
    }

    const activeRows = formData.filter(
      (t) => t.canonical_code.trim() || t.display_label.trim() || t.id
    );

    const codes = activeRows.map((t) => t.canonical_code.trim().toUpperCase());
    const duplicateIndex = codes.findIndex((c, i) => codes.indexOf(c) !== i);

    if (duplicateIndex !== -1) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate Canonical Code",
        text: `Canonical Code "${activeRows[
          duplicateIndex
        ].canonical_code.trim()}" is used more than once.`
      });
      return;
    }

    try {
      setLoading(true);

      if (isEditing) {
        const payload = buildPayload(formData[0]);
        await axios.put(`${API_URL}${editingId}/`, payload, {
          headers: { "Content-Type": "application/json" }
        });

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        for (const t of activeRows) {
          await axios.post(API_URL, buildPayload(t), {
            headers: { "Content-Type": "application/json" }
          });
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} term${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptyTerm }]);

      await fetchTerms();
    } catch (error) {
      console.error("Error saving terminology:", error);

      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: extractError(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptyTerm }]);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-term-page">
          <SettingsTabs />

          {!showForm ? (
            <>
              {/* ==================================================
                  LIST HEADER
              ================================================== */}

              <div className="tenant-term-page-header">
                <div>
                  <h2>Tenant Terminology</h2>
                </div>

                <button
                  type="button"
                  className="tenant-term-add-top-btn"
                  onClick={handleAdd}
                >
                  + Add Term
                </button>
              </div>

              {/* ==================================================
                  TABLE
              ================================================== */}

              <div className="tenant-term-list-card">
                {loading ? (
                  <div className="tenant-term-loading">
                    Loading terminology...
                  </div>
                ) : terms.length === 0 ? (
                  <div className="tenant-term-empty">
                    <div className="tenant-term-empty-icon">+</div>
                    <h3>No Terminology Found</h3>
                    <p>
                      There are no terminology entries available for the current
                      tenant.
                    </p>
                    <button
                      type="button"
                      className="tenant-term-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Term
                    </button>
                  </div>
                ) : (
                  <div className="tenant-term-table-wrapper">
                    <table className="tenant-term-table">
                      <thead>
                        <tr>
                          <th>Canonical Code</th>
                          <th>Display Label</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {terms.map((t) => (
                          <tr key={t.id}>
                            <td>
                              <span className="tenant-term-canonical-pill">
                                {t.canonical_code || "-"}
                              </span>
                            </td>

                            <td>
                              <strong>{t.display_label || "-"}</strong>
                            </td>

                            <td>
                              <div className="tenant-term-actions">
                                <button
                                  type="button"
                                  className="tenant-term-edit-btn"
                                  onClick={() => handleEdit(t)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="tenant-term-delete-btn"
                                  onClick={() => handleDelete(t)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ==================================================
                  ADD / EDIT HEADER
              ================================================== */}

              <div className="tenant-term-page-header">
                <div>
                  <h2>{isEditing ? "Edit Term" : "Create Terms"}</h2>
                </div>

                <button
                  type="button"
                  className="tenant-term-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              {/* ==================================================
                  FORM
              ================================================== */}

              <form onSubmit={handleSubmit}>
                {formData.map((t, index) => (
                  <div
                    className="tenant-term-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="tenant-term-form-body">
                      <div className="tenant-term-record-header">
                        <h4>Term {index + 1}</h4>

                        {!isEditing && formData.length > 1 && (
                          <button
                            type="button"
                            className="tenant-term-remove-btn"
                            onClick={() => handleRemoveForm(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="tenant-term-form-grid">
                        <div className="tenant-term-form-group">
                          <label>
                            Canonical Code <span>*</span>
                          </label>
                          <input
                            type="text"
                            name="canonical_code"
                            value={t.canonical_code}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. PROJECT"
                            list="canonical-code-suggestions"
                            maxLength={100}
                            required
                          />
                          <datalist id="canonical-code-suggestions">
                            {CANONICAL_CODE_SUGGESTIONS.map((v) => (
                              <option key={v} value={v} />
                            ))}
                          </datalist>
                        </div>

                        <div className="tenant-term-form-group">
                          <label>
                            Display Label <span>*</span>
                          </label>
                          <input
                            type="text"
                            name="display_label"
                            value={t.display_label}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. Assignment"
                            maxLength={100}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <button
                    type="button"
                    className="tenant-term-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another Term
                  </button>
                )}

                <div className="tenant-term-form-actions">
                  <button
                    type="button"
                    className="tenant-term-cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="tenant-term-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Term"
                      : "Save Terms"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTenantTerminology;